/**
 * ambientAudio — wrapper su @capacitor-community/native-audio per riprodurre
 * i suoni ambient (pioggia, onde, foresta, camino, rumore bianco) in loop
 * dentro l'app (sleep aid + alarm ringing).
 *
 * I file `.caf` (28s, PCM 16-bit mono 44.1kHz) sono in /public/sounds/, copiati
 * da Capacitor nel bundle iOS sotto `public/sounds/`. La native-audio risolve
 * il path con prefisso `public/` su iOS, mentre su web carica via fetch().
 */
import { Capacitor } from "@capacitor/core";
import { NativeAudio } from "@capacitor-community/native-audio";

// Mapping pubblico: assetId univoco → filename. NB: gli assetId NON devono
// avere caratteri speciali (es. trattini, spazi). Tutti lowercase ASCII.
export const AMBIENT_SOUNDS = [
  { id: "rain", name: "Pioggia leggera", file: "pioggia.caf" },
  { id: "ocean", name: "Onde del mare", file: "onde.caf" },
  { id: "forest", name: "Bosco di notte", file: "foresta.caf" },
  { id: "fireplace", name: "Camino acceso", file: "camino.caf" },
  { id: "whitenoise", name: "Rumore bianco", file: "rumore-bianco.caf" },
];

const preloaded = new Set();
let currentlyPlayingId = null;

function isNative() {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android";
}

function findById(id) {
  return AMBIENT_SOUNDS.find((s) => s.id === id) || null;
}

async function preloadOne(soundId) {
  if (preloaded.has(soundId)) return true;
  const def = findById(soundId);
  if (!def) return false;
  try {
    await NativeAudio.preload({
      assetId: soundId,
      assetPath: isNative()
        ? `public/sounds/${def.file}`
        : `/sounds/${def.file}`,
      audioChannelNum: 1,
      isUrl: false,
      // Preload con volume max; il vol può essere modulato in play() lato OS.
      volume: 1.0,
    });
    preloaded.add(soundId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Riproduce in loop il suono ambient con id `soundId`. Se un altro suono era
 * in riproduzione, lo ferma prima.
 */
export async function playAmbient(soundId) {
  await stopCurrent();
  const ok = await preloadOne(soundId);
  if (!ok) return false;
  try {
    await NativeAudio.loop({ assetId: soundId });
    currentlyPlayingId = soundId;
    return true;
  } catch {
    // Fallback: play singolo (alcune versioni del plugin non hanno loop su web)
    try {
      await NativeAudio.play({ assetId: soundId });
      currentlyPlayingId = soundId;
      return true;
    } catch {
      return false;
    }
  }
}

export async function stopCurrent() {
  if (!currentlyPlayingId) return;
  const id = currentlyPlayingId;
  currentlyPlayingId = null;
  try {
    await NativeAudio.stop({ assetId: id });
  } catch {
    /* swallow */
  }
}

export function getPlayingId() {
  return currentlyPlayingId;
}
